'use client';

import { useCallback, useMemo, useState } from 'react';
import { useAsync, useEvent } from 'react-use';
import { AudioStockItem } from 'search-gateway-client';
import { twMerge } from 'tailwind-merge';

import { AUDIO_EVENTS, PlayEvent, SeekEvent, TimeUpdateEvent } from '../events/AudioEvents';

export interface WaveformDataInterface {
    left: string[];
    right: string[];
}

export interface WaveformProps {
    stockItem: AudioStockItem;
    handleAudioItemClick: (isPlaying: boolean) => void;
    isPlaying: boolean;
    className?: string;
}

// Helper function to get max value from string array
const getMax = (arr: string[]): number => Math.max(...arr.map(parseFloat));

const getViewBoxHeight = (waveformData: WaveformDataInterface): number => {
    // determine max value for each stereo channel
    const leftMax = getMax(waveformData.left);
    const rightMax = getMax(waveformData.right);

    // use highest peak value * 2 to determine svg viewbox height
    return Math.ceil(Math.max(leftMax, rightMax) || 0) * 2;
};

const generateWaveformPathData = (waveformData: WaveformDataInterface): string => {
    // get values for each stereo channel
    const leftPeaks = waveformData.left;
    const rightPeaks = waveformData.right;

    // determine svg path data: this iterates over the values for each channel and creates an instruction for drawing a
    // line (upward or downward from the center of the box depending on channel). there are 1000 values for each channel
    // but we only render lines for 500
    // potentially could be optimized by only rendering lines for the visible area
    const leftPathData = leftPeaks
        .map((value, i) => (i % 2 === 0 ? `l 0,-${value} l 1,0 l 0,${value} l -1,0 m 2,0` : null))
        .filter(Boolean);

    const rightPathData = rightPeaks
        .map((value, i) => (i % 2 === 0 ? `l 0,${value} l 1,0 l 0,-${value} l -1,0 m 2,0` : null))
        .filter(Boolean);

    // determine viewBox height
    const boxHeight = getViewBoxHeight(waveformData);

    // setup svg viewBox params and join together path data from each channel
    return `M 0,${boxHeight / 2} ${leftPathData.join(' ')} M 0,${boxHeight / 2} ${rightPathData.join(' ')} Z`;
};

function getSliderValue(event: React.MouseEvent | React.KeyboardEvent): number {
    const position = event.currentTarget.getBoundingClientRect().left;
    if ('clientX' in event) {
        return (event.clientX - position) / event.currentTarget.clientWidth;
    }
    // For keyboard events, return current value or 0 as fallback
    return 0;
}

export default function Waveform({ stockItem, handleAudioItemClick, isPlaying, className }: WaveformProps) {
    const { id: stockItemId, previewUrl: audioUrl, duration, waveformUrl, title: stockItemTitle } = stockItem;

    const [currentTime, setCurrentTime] = useState(0);
    const [currentSrc, setCurrentSrc] = useState('');
    const [seekLineVisible, setSeekLineVisible] = useState(false);
    const [seekLineLeft, setSeekLineLeft] = useState('0');

    const { value: waveformData } = useAsync(async () => {
        const response = await fetch(waveformUrl);
        return (await response.json()) as WaveformDataInterface;
    }, [waveformUrl]);

    const moveSeekLine = (event: React.MouseEvent | React.KeyboardEvent) => {
        setSeekLineVisible(true);
        setSeekLineLeft(`${getSliderValue(event) * 100}%`);
    };

    const removeSeekLine = () => {
        setSeekLineVisible(false);
        setSeekLineLeft('0%');
    };

    const handlePlay = useCallback(
        (event: PlayEvent) => {
            if (event.src === audioUrl) {
                setCurrentSrc(event.src);
                if (event.time !== undefined) {
                    setCurrentTime(event.time);
                }
            }
        },
        [audioUrl],
    );

    const handleTimeUpdate = useCallback(
        (event: TimeUpdateEvent) => {
            if (event.src === audioUrl) {
                setCurrentTime(event.time!);
                setCurrentSrc(event.src);
            }
        },
        [audioUrl],
    );

    useEvent(AUDIO_EVENTS.PLAY, handlePlay as EventListener);
    useEvent(AUDIO_EVENTS.TIMEUPDATE, handleTimeUpdate as EventListener);

    const viewBox = useMemo(() => {
        return waveformData ? `0 0 1001 ${getViewBoxHeight(waveformData)}` : '0 0 1001 100';
    }, [waveformData]);

    const pathData = useMemo(() => {
        return waveformData ? generateWaveformPathData(waveformData) : '';
    }, [waveformData]);

    const handleProgressClicked = useCallback(
        (event: React.MouseEvent | React.KeyboardEvent) => {
            const percentage = getSliderValue(event);
            let position = duration * percentage;

            if (event.type === 'keydown' && 'key' in event) {
                position = currentTime;

                switch (event.key) {
                    case 'ArrowLeft':
                        position = Math.max(position - 1, 0);
                        break;
                    case 'ArrowRight':
                        position = Math.min(position + 1, duration!);
                        break;
                    case 'Home':
                        position = 0;
                        break;
                    case 'End':
                        event.preventDefault();
                        position = duration;
                        break;
                    case ' ':
                    case 'Enter':
                        event.preventDefault();
                        handleAudioItemClick(!isPlaying);
                        return;
                    default:
                        return;
                }
            }

            // Dispatch a custom event for audio to play at position
            window.dispatchEvent(new SeekEvent(audioUrl, position));

            // Also dispatch play event to ensure playback starts
            handleAudioItemClick(true);
        },
        [audioUrl, duration, currentTime, isPlaying, handleAudioItemClick],
    );

    // Calculate playback progress offset
    const offset = currentSrc === audioUrl && currentTime !== null && duration > 0 ? currentTime / duration : 0;
    const waveformGradientId = `progress-${stockItemId}`;
    const waveformWrapperClassNames = twMerge('flex-column w-full relative flex  w-full max-h-12 h-full', className);

    if (!waveformData) {
        return <div className={waveformWrapperClassNames} />;
    }

    return (
        <div
            role="slider"
            tabIndex={0}
            aria-valuenow={currentTime}
            className={waveformWrapperClassNames}
            onMouseMove={moveSeekLine}
            onMouseLeave={removeSeekLine}
            onClick={handleProgressClicked}
            onKeyDown={handleProgressClicked}
            aria-label="waveform"
        >
            <div
                className={`seek-line h-full w-0.5 bg-black absolute ${seekLineVisible ? 'block' : 'hidden'}`}
                style={{ left: seekLineLeft }}
            ></div>
            <svg
                className="w-full h-full"
                viewBox={viewBox}
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
            >
                <title>Audio Waveform for {stockItemTitle}</title>
                <defs>
                    <linearGradient id={waveformGradientId}>
                        <stop offset={offset} stopColor="var(--color-blue-700)" />
                        <stop offset={offset} stopColor="var(--color-gray-300)" />
                    </linearGradient>
                </defs>
                <path d={pathData} fill={`url(#${waveformGradientId})`} />
            </svg>
        </div>
    );
}
